import React, { Component, ReactNode } from "react"
import { Box, Center, Text, Button, VStack, HStack, Card, Code } from "@chakra-ui/react"
import { FaExclamationTriangle, FaRedo, FaBug } from "react-icons/fa"
import { EMAILS } from "@hydrologiq/shared/branding"

interface SectionErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  retryCount: number
}

interface SectionErrorBoundaryProps {
  children: ReactNode
  sectionName: string
  onRetry?: () => void
  fallback?: ReactNode
}

export class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, SectionErrorBoundaryState> {
  private retryTimeoutId: NodeJS.Timeout | null = null

  constructor(props: SectionErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<SectionErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[SectionErrorBoundary] Error in section ${this.props.sectionName}:`, error, errorInfo)

    this.setState({
      error,
      errorInfo,
    })

    // Report error to monitoring service if available
    if (window.gtag) {
      window.gtag("event", "exception", {
        description: `Section ${this.props.sectionName} error: ${error.message}`,
        fatal: false,
      })
    }
  }

  componentDidUpdate(prevProps: SectionErrorBoundaryProps) {
    // Reset error state when section changes
    if (prevProps.sectionName !== this.props.sectionName && this.state.hasError) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: 0,
      })
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
    }
  }

  handleRetry = () => {
    const { onRetry } = this.props
    const { retryCount } = this.state

    // Increment retry count
    this.setState({
      retryCount: retryCount + 1,
    })

    // Clear error state to trigger re-render
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })

    // Call custom retry handler if provided
    if (onRetry) {
      onRetry()
    }

    // If retry fails again, we'll catch it in componentDidCatch
  }

  handleReportIssue = () => {
    const { sectionName } = this.props
    const { error, errorInfo } = this.state

    const errorDetails = {
      section: sectionName,
      error: error?.message || "Unknown error",
      stack: error?.stack || "No stack trace",
      componentStack: errorInfo?.componentStack || "No component stack",
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      retryCount: this.state.retryCount,
    }

    // Create a mailto link with error details
    const subject = encodeURIComponent(`Section Loading Error: ${sectionName}`)
    const body = encodeURIComponent(
      `
Section Loading Error Report

Section: ${sectionName}
Error: ${error?.message || "Unknown error"}
Retry Count: ${this.state.retryCount}
Timestamp: ${errorDetails.timestamp}
User Agent: ${errorDetails.userAgent}

Error Stack:
${error?.stack || "No stack trace available"}

Component Stack:
${errorInfo?.componentStack || "No component stack available"}

Please describe what you were doing when this error occurred:
[Your description here]
    `.trim()
    )

    window.open(`mailto:${EMAILS.support}?subject=${subject}&body=${body}`)
  }

  render() {
    const { hasError, error, retryCount } = this.state
    const { children, sectionName, fallback } = this.props

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback
      }

      const isNetworkError = error?.message?.includes("fetch") || error?.message?.includes("network")
      const isTimeoutError = error?.message?.includes("timeout")
      const hasRetriedMultipleTimes = retryCount >= 3

      return (
        <Center minH="400px" p={6}>
          <Card.Root maxW="600px" w="100%">
            <Card.Body>
              <VStack gap={6} align="center">
                <Box color="red.500" fontSize="4xl">
                  <FaExclamationTriangle />
                </Box>

                <VStack gap={2} textAlign="center">
                  <Text fontSize="xl" fontWeight="bold" color="red.500">
                    Failed to Load {sectionName.charAt(0).toUpperCase() + sectionName.slice(1)} Section
                  </Text>

                  {isNetworkError && (
                    <Text color="fg.muted">
                      This appears to be a network connectivity issue. Please check your internet connection.
                    </Text>
                  )}

                  {isTimeoutError && (
                    <Text color="fg.muted">
                      The section took too long to load. This might be due to a slow connection or server issues.
                    </Text>
                  )}

                  {!isNetworkError && !isTimeoutError && (
                    <Text color="fg.muted">An unexpected error occurred while loading this section.</Text>
                  )}

                  {hasRetriedMultipleTimes && (
                    <Text color="orange.600" fontSize="sm">
                      Multiple retry attempts have failed. Please try refreshing the page or contact support.
                    </Text>
                  )}
                </VStack>

                <HStack gap={4}>
                  <Button onClick={this.handleRetry} colorPalette="blue" disabled={hasRetriedMultipleTimes}>
                    <FaRedo />
                    {retryCount > 0 ? `Retry (${retryCount})` : "Retry"}
                  </Button>

                  <Button onClick={this.handleReportIssue} variant="outline" colorPalette="gray">
                    <FaBug />
                    Report Issue
                  </Button>
                </HStack>

                {/* Developer information (only in development) */}
                {import.meta.env.VITE_ENV === "dev" && error && (
                  <Box w="100%" mt={4}>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>
                      Developer Info:
                    </Text>
                    <Code
                      display="block"
                      whiteSpace="pre-wrap"
                      p={3}
                      fontSize="xs"
                      maxH="200px"
                      overflow="auto"
                      bg="bg.subtle"
                    >
                      {error.message}
                      {error.stack && `\n\nStack:\n${error.stack}`}
                    </Code>
                  </Box>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>
        </Center>
      )
    }

    return children
  }
}

// Hook version for functional components
export const useSectionErrorHandler = (sectionName: string) => {
  const handleError = React.useCallback(
    (error: Error, errorInfo: React.ErrorInfo) => {
      console.error(`[useSectionErrorHandler] Error in section ${sectionName}:`, error, errorInfo)

      // Report to monitoring service
      if (window.gtag) {
        window.gtag("event", "exception", {
          description: `Section ${sectionName} error: ${error.message}`,
          fatal: false,
        })
      }
    },
    [sectionName]
  )

  return { handleError }
}
